import { ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from '../auth.service';
import { BaseComponent } from '../base/base.component';
import { Assessment, StudentAssessmentDetails } from '../data/assessments';
import { DataService } from '../data/data.service';
import { Location } from '@angular/common';
import { OrganizationService } from '../organization.service';
import { RedirectService } from '../redirect.service';
import { Student } from '../data/student';
import { ISortOrder } from '../sort-toggle/sort-toggle.component';
import { RequestAssessmentModalComponent } from '../request-assessment-modal/request-assessment-modal.component';
import { ClassAssessmentDetailsModalComponent } from '../class-assessment-details-modal/class-assessment-details-modal.component';

@Component({
  selector: 'app-class-assessments',
  templateUrl: './class-assessments.component.html',
  styleUrls: ['./class-assessments.component.scss']
})
export class ClassAssessmentsComponent extends BaseComponent implements OnInit {

  constructor(route: ActivatedRoute,
    authService: AuthService,
    organizationService: OrganizationService,
    redirectService: RedirectService,
    router: Router,
    dataService: DataService,
    location: Location,
    private changeDetectorRef: ChangeDetectorRef,
    private modalService: NgbModal) {
    super(route, authService, organizationService, redirectService, router, dataService, location);
  }

  @Input() classId: number;
  @Input() className: string;
  @Input() courseCode: string;
  @Input() students: Student[];

  @ViewChild('requestAssessment', { static: false }) requestAssessmentModal: RequestAssessmentModalComponent;
  @ViewChild('assessmentDetails', { static: false }) assessmentDetailsModal: ClassAssessmentDetailsModalComponent;

  activeModal: NgbModalRef;
  selectedAssessment: Assessment;
  selectedAssessmentResults: StudentAssessmentDetails[];

  assessmentCategories: AssessmentCategory[] = [];
  currentAssessmentCategory: AssessmentCategory;
  defaultCategory: string = "Basic Skills"

  get assessments(): Assessment[] {
    return this.currentAssessmentCategory?.Assessments ?? [];
  }

  allAssessmentResults: StudentAssessmentDetails[];

  assessmentDefinitionsLoaded: boolean = false;
  classAssessmentsLoaded: boolean = false;

  ngOnInit() {
    this.dataService.getAssessments().subscribe(result => {
      // group assessments data by category
      var uniqueCategories = [...new Set(result.map(x => x.Category))];

      for (var category of uniqueCategories) {
        this.assessmentCategories.push({ CategoryName: category, Assessments: result.filter(x => x.Category === category) });
      }

      var categoryToSelect = uniqueCategories.includes(this.defaultCategory) ? this.defaultCategory : uniqueCategories[0];
      this.selectAssessmentCategory(categoryToSelect);

      this.assessmentDefinitionsLoaded = true;
    })
    this.refreshClassAssessmentsData();
  }

  refreshClassAssessmentsData() {
    this.classAssessmentsLoaded = false;

    this.dataService.getClassAssessmentResults(this.classId).subscribe(result => {
      this.allAssessmentResults = result;
      this.allAssessmentResults.forEach(x => {
        if (x.ResultsJson) x.ParsedResults = JSON.parse(x.ResultsJson);
      });

      this.classAssessmentsLoaded = true;
    });
  }

  selectAssessmentCategory(category: string): void {
    var currentCategoryIndex = this.assessmentCategories.findIndex(x => x.CategoryName === category);
    this.currentAssessmentCategory = this.assessmentCategories[currentCategoryIndex];
    console.log(this.currentAssessmentCategory);
  }

  getResultsForAssessment(assessmentID: number) {
    return this.allAssessmentResults.filter(x => x.AssessmentID === assessmentID);
  }

  getNumPassed(assessmentID: number,) {
    var results = this.getResultsForAssessment(assessmentID);
    return results.filter(x => x.Passed).length;
  }

  getNumAttempted(assessmentID: number) {
    var results = this.getResultsForAssessment(assessmentID);
    return results.filter(x => x.Passed === false).length; // "Passed" will be false, but not undefined
  }

  getNumNotTaken(assessmentID: number) {
    var results = this.getResultsForAssessment(assessmentID);
    return results.filter(x => x.Passed == null).length; // use == instead of === here to handle both null and undefined case
  }

  updateSort(sortOrder: ISortOrder) {
    this.assessments.sort((a, b) => {
      var sortVal =
        sortOrder.orderBy === 'AssessmentName' ? this.sortString(a.Title, b.Title) :
          sortOrder.orderBy === 'NumPassed' ? this.sortNumbers(this.getNumPassed(a.ID), this.getNumPassed(b.ID)) :
            sortOrder.orderBy === 'NumAttempted' ? this.sortNumbers(this.getNumAttempted(a.ID), this.getNumAttempted(b.ID)) :
              sortOrder.orderBy === 'NumNotTaken' ? this.sortNumbers(this.getNumNotTaken(a.ID), this.getNumNotTaken(b.ID)) :
                this.sortString(a.Title, b.Title);
      if (!sortOrder.ascending) sortVal *= -1;
      return sortVal;
    });
  }

  sortNumbers(numA, numB) {
    var cleanNumA = this.getUndefinedCheck(numA) ? -1 : numA;
    var cleanNumB = this.getUndefinedCheck(numB) ? -1 : numB;
    return cleanNumA - cleanNumB;
  }

  sortString(stringA, stringB) {
    var stringNameA = this.getUndefinedCheckString(stringA) ? "" : stringA;
    var stringNameB = this.getUndefinedCheckString(stringB) ? "" : stringB;
    return stringNameA.localeCompare(stringNameB);
  }

  getUndefinedCheck(value: number): boolean {
    return typeof value === 'undefined' || value === null;
  }

  getUndefinedCheckString(value: string): boolean {
    return value === "" || value === null || typeof value === 'undefined'
  }

  selectAssessment(assessmentID: number) {
    this.selectedAssessment = assessmentID ? this.assessments?.filter(x => x.ID === assessmentID)[0] : null;
    this.selectedAssessmentResults = assessmentID ? this.allAssessmentResults?.filter(x => x.AssessmentID === assessmentID) : [];

    this.changeDetectorRef.detectChanges();
  }

  showAssessmentDetailsModal(assessmentID: number) {
    this.selectAssessment(assessmentID);
    this.assessmentDetailsModal.open();
  }

  showAssessmentRequestModal(assessmentID: number) {
    this.selectAssessment(assessmentID);
    this.requestAssessmentModal.open();
  }
}

class AssessmentCategory {
  CategoryName: string;
  Assessments: Assessment[];
}